import NaturalPersonController from '@controllers/NaturalPersonController'
import { router } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'

type NaturalPersonData = App.Data.User.NaturalPersonData

export type SpouseData = NaturalPersonData & {
  has_marital_contract?: boolean | null
  has_separate_assessment?: boolean | null
}

export type ChildData = NaturalPersonData & {
  receives_child_support?: boolean | null
}

export const SPOUSE_KEY = 'spouse'

const reloadKeys = ['person', 'spouse', 'children', 'profileCompletion']

const reload = (only: string[]) => () => router.reload({ only })

export function useHouseholdMembers(household: { spouse?: SpouseData | null; children?: ChildData[] }) {
  const children = computed<ChildData[]>(() => household.children ?? [])

  const selectedKey = ref<string | null>(null)
  const pendingSelection = ref<'spouse' | 'child' | null>(null)

  const selectedChildIndex = computed(() => children.value.findIndex((child) => child.id === selectedKey.value))
  const showsSpouse = computed(() => selectedKey.value === SPOUSE_KEY && Boolean(household.spouse))

  const keyExists = (key: string | null) =>
    key === SPOUSE_KEY ? Boolean(household.spouse) : children.value.some((child) => child.id === key)

  function ensureSelection(): void {
    if (keyExists(selectedKey.value)) {
      return
    }

    selectedKey.value = household.spouse ? SPOUSE_KEY : (children.value[0]?.id ?? null)
  }

  ensureSelection()

  watch(
    () => household.spouse,
    () => {
      if (pendingSelection.value === 'spouse' && household.spouse) {
        selectedKey.value = SPOUSE_KEY
        pendingSelection.value = null
      }

      ensureSelection()
    },
  )

  watch(
    () => household.children,
    () => {
      if (pendingSelection.value === 'child' && children.value.length > 0) {
        selectedKey.value = children.value[children.value.length - 1].id ?? null
        pendingSelection.value = null
      }

      ensureSelection()
    },
  )

  function addMember(type: 'partner' | 'child', only: string[]): void {
    pendingSelection.value = type === 'partner' ? 'spouse' : 'child'

    router.post(
      NaturalPersonController.store().url,
      { type },
      { preserveScroll: true, preserveState: true, onSuccess: reload(only) },
    )
  }

  const addSpouse = () => addMember('partner', ['spouse', 'profileCompletion'])
  const addChild = () => addMember('child', ['person', 'children', 'profileCompletion'])

  function removeMember(id?: string): void {
    if (!id) {
      return
    }

    router.delete(NaturalPersonController.destroy(id).url, { preserveScroll: true, onSuccess: reload(reloadKeys) })
  }

  return {
    children,
    selectedKey,
    selectedChildIndex,
    showsSpouse,
    addSpouse,
    addChild,
    removeMember,
  }
}
